"use client";
import { useGlobalContent } from "@app/[locale]/GlobalContentProvider";
import RecentProducts from "@components/commerce/product/recently-viewed/RecentProducts";
import { useYotpoReviews } from "@components/commerce/reviews/yotpo/useYotpoReviews";
import { activeSiteConfig } from "@config/site/site-config";
import type { ProductPageConfigurationModel } from "@lib/contentful/mappers/mapProductPageConfiguration";
import { useCart } from "@hooks/useCart";
import { useOrderItemsLimit } from "@hooks/useOrderItemsLimit";
import { AnalyticsClient } from "@lib/analytics/AnalyticsClient";
import { useProduct } from "@lib/commerce/hooks/productOperations";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import type { ProductVariant } from "@lib/product/types";
import type { AddToCartSource } from "@lib/analytics/analyticsTypes";
import { useErrorToast } from "@lib/utils/store-utility/storeUtility";
import { useLock } from "@store/hooks";
import type { AlgoliaRelatedProduct } from "@lib/wrappers/algolia/types";
import { useRouteChange } from "@store/hooks/useRouteChange";
import { Box } from "@ecco/ui/box";
import { useLocale } from "next-intl";
import dynamic from "next/dynamic";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useStore } from "@store/index";
import { isFibblEnabled } from "@components/fibbl/constants";
import { AlgoliaRecommendModel } from "@lib/algolia/types";
import type { Recommendations } from "@lib/algolia/utils";
import { useRecentlyViewedProducts } from "@components/commerce/product/recently-viewed/useRecentlyViewedProducts";
import ProductRecommendations from "@components/commerce/product/recommendations/DeferredProductRecommendations";
import { PRODUCT_CAROUSEL_DEFAULT_TILE_WIDTHS } from "@lib/constants/carousel";
import { ProductPurchaseSection } from "./ProductPurchaseSection";

const YotpoReviewsWidget = dynamic(
    () => import("@components/commerce/reviews/yotpo/yotpo-reviews-widget"),
    { ssr: false }
);

const ProductBuyCardSticky = dynamic(
    () => import("@components/ui/modals/product-buy-card-sticky/ProductBuyCardSticky"),
    { ssr: false }
);

const Hotspot = dynamic(
    () => import("@components/cms/hotspot/Hotspot").then((mod) => mod.Hotspot),
    { ssr: false }
);
const Video = dynamic(() => import("@components/cms/video/Video").then((mod) => mod.Video), {
    ssr: false,
});
const StoryTileGrid = dynamic(
    () => import("@components/cms/story-tile-grid/StoryTileGrid").then((mod) => mod.StoryTileGrid),
    { ssr: false }
);
const BazaarvoiceGalleryContainer = dynamic(
    () =>
        import("@components/cms/bazaarvoice-gallery-container/BazaarvoiceGalleryContainer").then(
            (mod) => mod.BazaarvoiceGalleryContainer
        ),
    { ssr: false }
);

const FibblScript = dynamic(() => import("@lib/analytics/FibblScript"), {
    ssr: false,
});

interface ProductDetailPageProps {
    articleNumber: string;
    colorCode: string;
    relatedProduct?: AlgoliaRelatedProduct;
    recommendations: Recommendations;
    notFoundFallback: ReactNode;
    productPageConfiguration: ProductPageConfigurationModel;
    reviewTotalScore?: number;
    requiredInformation?: ReactNode;
}

export default function ProductDetailPageClient({
    articleNumber,
    colorCode,
    relatedProduct,
    recommendations,
    notFoundFallback,
    productPageConfiguration,
    reviewTotalScore,
    requiredInformation,
}: Readonly<ProductDetailPageProps>) {
    const lock = useLock();
    const setAddToCartIsExecuted = useStore((state) => state.setAddToCartIsExecuted);
    const setIsOpenAddedToBagModal = useStore((state) => state.setIsOpenAddedToBagModal);
    const locale = useLocale();
    const errorToaster = useErrorToast();
    const { getMultiple } = useMicrocopy();
    const headlineProductNameRef = useRef<HTMLHeadingElement>(null);

    const activeProduct = useStore((state) => state.data.pdpState.activeProduct);
    const setActiveProduct = useStore((state) => state.setActiveProduct);

    const { product, defaultPrice, isFetching } = useProduct({
        sku: `${articleNumber}${activeProduct?.colorCode ?? colorCode}`,
        locale,
    });

    const { country } = activeSiteConfig;
    const [addToCartFailed, setAddToCartFailed] = useState<boolean>(false);
    const [isSticky, setIsSticky] = useState(false);
    const handleStickyChange = (sticky: boolean) => setIsSticky(sticky);
    const { addToCart } = useCart();
    const { siteSettings } = useGlobalContent();
    const { theme } = siteSettings.promotions;

    useEffect(() => {
        setIsOpenAddedToBagModal(false);
    }, []);

    useOrderItemsLimit(country);
    useRecentlyViewedProducts(product?.sku);
    const { isYotpoReviewsEnabled } = useYotpoReviews();

    const microcopies = useMemo(() => {
        const shared = getMultiple(CommonCMS.shared, [
            CommonCMS.error.general.title,
            CommonCMS.error.general.description,
        ]);
        const page = getMultiple(CommonCMS.page, [
            CommonCMS.youMightAlsoLikeMC,
            CommonCMS.recentlyViewedMC,
            CommonCMS.lookingSimilarMC,
        ]);
        return {
            errorTitle: shared[CommonCMS.error.general.title],
            errorDescription: shared[CommonCMS.error.general.description],
            youMightAlsoLike: page[CommonCMS.youMightAlsoLikeMC],
            recentlyViewed: page[CommonCMS.recentlyViewedMC],
            lookingSimilar: page[CommonCMS.lookingSimilarMC],
        };
    }, []);

    useEffect(() => {
        if (!product) return;

        AnalyticsClient.viewItem(product, relatedProduct?.variants);
    }, [product?.sku]);

    useRouteChange(
        () => {
            setActiveProduct(null);
        },
        { ignoreSearchParams: true }
    );

    const handleAddToCartWithAnalytics = lock(
        async (variant: ProductVariant, analytics: boolean = true, source?: AddToCartSource) => {
            setAddToCartIsExecuted(false);
            setIsOpenAddedToBagModal(true);
            if (!variant?.sku) {
                return;
            }

            if (addToCartFailed) {
                setAddToCartFailed(false);
            }

            try {
                const { errors } = await addToCart(variant?.sku);

                if (errors?.[0]) {
                    setAddToCartIsExecuted(false);
                    setAddToCartFailed(true);
                    handleCartError(errors[0]);
                } else {
                    setAddToCartIsExecuted(true);
                }

                if (analytics) {
                    AnalyticsClient.addToCart(product, variant, source);
                }
            } catch (error) {
                // Handle any unexpected errors
                Logger.error(
                    ServiceType.COMMERCE_TOOLS,
                    "Add to cart operation failed with unexpected error",
                    {
                        tag: LogTag.SHOPPING_BAG,
                        error,
                    }
                );
                setAddToCartIsExecuted(false);
                setAddToCartFailed(true);
                handleCartError({ message: "An unexpected error occurred" });
            }
        }
    );

    const handleStickyAddToCart = (variant: ProductVariant) =>
        handleAddToCartWithAnalytics(variant, true, "sticky");

    const handleCartError = (error: { message: string }) => {
        Logger.error(ServiceType.COMMERCE_TOOLS, "addToCart updateExec error", {
            tag: LogTag.SHOPPING_BAG,
            error,
        });
        errorToaster({ title: error.message });
    };

    const {
        calloutDiscountTitle = "",
        calloutDiscountDescription = "",
        hotspot,
        video,
        tileGrid,
        bazaarvoiceGallery,
    } = productPageConfiguration;

    if (!product) {
        return notFoundFallback;
    }

    return (
        <>
            <div style={{ display: "none" }} aria-hidden="true" data-testid="ProductDetailPage" />
            {isFibblEnabled && <FibblScript locale={locale} />}
            <ProductPurchaseSection
                product={product}
                defaultPrice={defaultPrice}
                onAddToCart={handleAddToCartWithAnalytics}
                calloutDiscountTitle={calloutDiscountTitle}
                calloutDiscountDescription={calloutDiscountDescription}
                relatedProduct={relatedProduct}
                isFetchingProduct={isFetching}
                recommendations={recommendations}
                headlineProductNameRef={headlineProductNameRef}
                onStickyChange={handleStickyChange}
            />
            <ProductRecommendations
                theme={theme}
                headline={microcopies.youMightAlsoLike}
                recommendations={recommendations.relatedProducts}
                carouselHeight={["auto", "100vw", "68vw", "52vw", "49vw", "900px"]}
                tileWidth={[...PRODUCT_CAROUSEL_DEFAULT_TILE_WIDTHS, "calc(100vw/3.5)", "550px"]}
                model={AlgoliaRecommendModel.RELATED_PRODUCTS}
            />
            {bazaarvoiceGallery && <BazaarvoiceGalleryContainer {...bazaarvoiceGallery} />}
            {hotspot && <Hotspot {...hotspot} />}
            {tileGrid && (
                <Box mb={6}>
                    <StoryTileGrid {...tileGrid} />
                </Box>
            )}
            {video && (
                <Box my={6}>
                    <Video {...video} />
                </Box>
            )}
            <RecentProducts theme={theme} headline={microcopies.recentlyViewed} />
            {isYotpoReviewsEnabled && (
                <YotpoReviewsWidget product={product} totalScore={reviewTotalScore} />
            )}
            {requiredInformation}
            {isSticky && (
                <ProductBuyCardSticky
                    locale={locale}
                    product={product}
                    initialProductPrice={defaultPrice}
                    onAddToCart={handleStickyAddToCart}
                    isOpen={isSticky}
                    headlineProductNameRef={headlineProductNameRef}
                    recommendations={recommendations.similarProducts}
                />
            )}
        </>
    );
}
